import React from "react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    listAdminProducts,
    listAdminQuotes,
    listAdminContacts,
} from "@/lib/api";
import { ShoppingBag, MessageSquare, Mail, TrendingUp } from "lucide-react";

const Dashboard = () => {
    const { data: products = [] } = useQuery({
        queryKey: ["admin", "products"],
        queryFn: listAdminProducts,
    });
    const { data: quotes = [] } = useQuery({
        queryKey: ["admin", "quotes"],
        queryFn: listAdminQuotes,
    });
    const { data: contacts = [] } = useQuery({
        queryKey: ["admin", "contacts"],
        queryFn: listAdminContacts,
    });

    const stats = [
        { label: "Produits", value: products.length, icon: ShoppingBag, color: "bg-[#0a84ff]" },
        { label: "Demandes de devis", value: quotes.length, icon: MessageSquare, color: "bg-[#00e5ff]" },
        { label: "Messages contact", value: contacts.length, icon: Mail, color: "bg-emerald-500" },
        { label: "Taux conversion", value: "—", icon: TrendingUp, color: "bg-neutral-700" },
    ];

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">
                        Tableau de bord
                    </h1>
                    <p className="text-neutral-500 mt-1">
                        Vue d'ensemble de votre plateforme Dentiprime.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {stats.map((s) => {
                        const Icon = s.icon;
                        return (
                            <Card key={s.label}>
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-neutral-500">
                                        {s.label}
                                    </CardTitle>
                                    <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center`}>
                                        <Icon className="w-4 h-4 text-white" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-display font-semibold">
                                        {s.value}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display text-lg">Dernières demandes de devis</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {quotes.slice(0, 5).map((q) => (
                                <div
                                    key={q.id}
                                    className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0"
                                >
                                    <div>
                                        <div className="font-medium text-sm">{q.full_name}</div>
                                        <div className="text-xs text-neutral-500">
                                            {q.items?.length || 0} article(s)
                                        </div>
                                    </div>
                                    <div className="text-xs text-neutral-400">
                                        {new Date(q.created_at).toLocaleDateString("fr-FR")}
                                    </div>
                                </div>
                            ))}
                            {quotes.length === 0 && (
                                <div className="text-sm text-neutral-500">Aucune demande pour le moment.</div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="font-display text-lg">Derniers messages</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {contacts.slice(0, 5).map((c) => (
                                <div
                                    key={c.id}
                                    className="flex items-center justify-between gap-3 py-2 border-b border-neutral-100 last:border-0"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-sm">{c.full_name}</div>
                                        <div className="text-xs text-neutral-500 truncate">
                                            {c.message}
                                        </div>
                                    </div>
                                    <div className="text-xs text-neutral-400 whitespace-nowrap">
                                        {new Date(c.created_at).toLocaleDateString("fr-FR")}
                                    </div>
                                </div>
                            ))}
                            {contacts.length === 0 && (
                                <div className="text-sm text-neutral-500">Aucun message pour le moment.</div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Dashboard;
